// Admin-Wünsche W3 (output/admin-wuensche-20261008/PLAN.md 2.3, Schritt P-3): die Zeilen „Zugehörigkeit und Zugang“ im
// Profil in Worten. Reine Funktionen über Serverfelder (account.rolle, account.twoFactor, access, createdAt,
// previousLoginAt aus GET /api/auth/profile; KontingentStand aus GET /api/administration/billing/kontingent); sie
// entscheiden nichts neu (M-01.6). Die Rolle wird nur über BESITZER und MITGLIED unterschieden, der übrige Fall ist der
// Administrator: in pages/einstellungen steht kein Rollenname (AE-03, frontend/tests/ebenen-woerter.test.mjs). Die Begriffe
// der Organisation kommen aus der Marke (MK-1c); Sätze nennen den Namen der Organisation statt eines Artikels.
// Geprüft in frontend/tests/profil-zeilen.test.mjs.
import type { AccountFlags, PersonalAccess } from '../../administration/api';
import type { KontingentStand } from '../../administration/kontingent';
import { kontoRollenWort } from '../../administration/rollenWort';
import { NICHT_LESBAR } from '../../config/ebenen';
import { PROFIL_TEXTE } from './kontoTexte';
import { HILFE_SLUG, ROLLEN_HILFE_ANKER } from './seiten';
import { datum, datumZeit } from '../../utils/datum';

export type ProfilBegriffe = { tenantLabel: string; adminLabel: string; memberLabel: string; planLabel: string };
export type ProfilWeg = { slug: string; text: string };
/** laedt (Plan 1a Welle 2, Ä-23): nur gesetzt, solange die Zeile ihren Ladetext zeigt. Seit PF-08 (B4.3) lädt keine Zeile des
 *  Profils mehr nach (die Zeile „Nutzung in diesem Monat“ ist entfallen); das Feld bleibt für die Aufrufer. */
export type ProfilZeile = { titel: string; wert: string; weg: ProfilWeg | null; laedt?: true };

// Plan 1a Welle 2, Spur user (Ä-13 Punkt 2, UQ-01): das Zustandswort eines Kontos einer Firma ohne Lizenzplatz, an einer
// Stelle – die Karte der Startseite, „Mein Zugang“ (pages/accessSummary.ts OHNE_PLATZ) und die Profilzeile „Nutzung in diesem
// Monat“ nehmen es von hier. istOhnePlatz: Zustand ohne Platz, mit Organisation, kein Einzelkonto.
export const OHNE_LIZENZPLATZ = 'Ohne Lizenzplatz';
export const istOhnePlatz = (access: PersonalAccess | null | undefined): boolean =>
  !!access && access.status === 'NO_SEAT' && !!access.organization && access.organization.kind !== 'SOLO';

/** „12.08.2026“ (utils/datum.ts); ohne Wert ein Strich. */
export const datumText = (iso: string | null | undefined) => iso ? datum(iso) : '—';

/** „07.10.2026, 18:42“; ohne frühere Anmeldung der Satz dazu (AW-10). */
export function vorherigeAnmeldungText(iso: string | null | undefined): string {
  if (!iso) return 'Noch keine frühere Anmeldung gespeichert.';
  return datumZeit(iso);
}

/** „Besitzer“, „Administrator (eingeschränkt: Buchhaltung)“, „Mitglied · Zusatzrolle Recherche“. Plan 1a MA-02 (W-15): die
 *  Besitzerin oder der Besitzer heißt überall nur „Besitzer“, auch mit Firma; das Wort kommt aus administration/rollenWort.ts
 *  (kontoRollenWort). einzelkonto bleibt für die Aufrufer, ändert das Wort aber nicht mehr. */
export function rolleText(rolle: AccountFlags['rolle'] | null | undefined, begriffe: ProfilBegriffe, einzelkonto: boolean): string {
  const grund = kontoRollenWort(rolle, begriffe);
  if (!rolle || grund === null) return `Ohne ${begriffe.tenantLabel}`;
  if (rolle.eingeschraenkt) return `${grund} (eingeschränkt${rolle.zusatzrolle ? `: ${rolle.zusatzrolle}` : ''})`;
  return rolle.zusatzrolle ? `${grund} · Zusatzrolle ${rolle.zusatzrolle}` : grund;
}

export function organisationText(access: PersonalAccess | null | undefined, begriffe: ProfilBegriffe): string {
  const organisation = access?.organization;
  if (!organisation) return `Ohne ${begriffe.tenantLabel}`;
  return organisation.kind === 'SOLO' ? 'Einzelkonto' : organisation.name;
}

/** „Pro · jährlich“; ohne Abrechnungsintervall nur der Name. */
export function tarifText(name: string, access: PersonalAccess | null | undefined): string {
  const intervall = access?.organization?.billingInterval;
  return intervall ? `${name} · ${intervall === 'YEAR' ? 'jährlich' : 'monatlich'}` : name;
}

/** „Zugewiesen seit 03.09.2026“ oder „Kein Platz“; wie „Mein Zugang“ für Plattformkonten und Konten ohne Organisation. */
export function platzText(access: PersonalAccess | null | undefined): string {
  if (!access) return '—';
  // Plan 1a Welle 1 (W1-O06): „Lizenzplatz“ wie die Zeile darüber, nicht „Rechercheplatz“.
  if (access.status === 'PLATFORM_BLOCKED') return 'Kein Lizenzplatz (Plattformkonto)';
  if (!access.organization) return 'Einzelzugang';
  if (!access.seatActive) return 'Kein Platz';
  return access.licensedAt ? `Zugewiesen seit ${datumText(access.licensedAt)}` : 'Zugewiesen';
}

/** „Eingeschaltet“, „Aus“ oder „Von Talblick Rechtsanwälte verlangt, noch nicht eingerichtet“; ein Konto ohne eigenes
 *  Passwort meldet sich über seine Organisation an, deren Anmeldung regelt den zweiten Faktor (AA-F10). */
export function zweiFaktorText(account: Pick<AccountFlags, 'twoFactor' | 'hasPassword'> | null | undefined, organisation: string | null): string {
  if (!account) return '—';
  if (account.twoFactor.aktiv) return 'Eingeschaltet';
  if (!account.hasPassword) return organisation ? `Über die Anmeldung von ${organisation}` : 'Aus';
  if (account.twoFactor.pflicht) return organisation ? `Von ${organisation} verlangt, noch nicht eingerichtet` : 'Verlangt, noch nicht eingerichtet';
  return 'Aus';
}

/** Die Profilzeile „Nutzung in diesem Monat“ (Schlusslauf 2, M3; Regel unten nach KT-23).
 *  Plan 1a Welle 2 (UQ-01): ohnePlatz geht vor – ein Konto ohne Lizenzplatz liest „Ohne Lizenzplatz“ für jeden Stand (geladen,
 *  nicht geladen, Fehler, mit eigener Grenze), nie „Keine persönliche Grenze“ neben „Lizenzplatz: Kein Platz“. */
export const NUTZUNG_LAEDT = 'Wird geladen …';
/** Nachbesserung I/Z-02 (VORAB-MESSUNG 7.2 Nr 2d; welle-2/nachbesserung/AUFTEILUNG.md A-7, W-14): firmenNutzung ist für ein
 *  Konto einer Firma der Text von „Mein Zugang“ › „Nutzung in diesem Monat“ (pages/accessSummary.ts nutzungKurz), den der
 *  Aufrufer rechnet, weil accessSummary.ts diese Datei lädt (sonst ein Ladekreis). Er gilt ohne Lizenzplatz sofort und sonst,
 *  sobald der Stand da ist; Ladezeile und Fehlerzeile bleiben. Ohne die Angabe (Einzelkonto, Konto ohne Firma) wie bisher. */
/** Spur kontingent, KT-23 „Profil, feste Regel statt Beispiel“ (SPEC-KONTINGENT.md 4.1): ohne persönliche Grenze „2 % des
 *  Kontingents verwendet“ bzw. „unter 1 % des Kontingents verwendet“, mit persönlicher Grenze „72 % der persönlichen Grenze
 *  verwendet“, an der Grenze „Grenze erreicht“. Die Spec gibt hier ihre eigene Regel vor; der Text von „Mein Zugang“
 *  (firmenNutzung, Nachbesserung I/Z-02) gilt deshalb nur noch ohne Lizenzplatz (welle-2/kontingent/AUFTEILUNG.md R9 b,
 *  ABWEICHUNGEN.md AW-7). */
export function nutzungText(stand: KontingentStand | null | undefined, fehler: boolean, ohnePlatz = false, firmenNutzung?: string | null): string {
  if (ohnePlatz) return firmenNutzung ?? OHNE_LIZENZPLATZ;
  if (fehler) return 'Gerade nicht verfügbar';
  if (!stand) return NUTZUNG_LAEDT;
  const { kosten, recherchen, vomKontingent } = stand.person;
  if (kosten) return kosten.zustand === 'ERREICHT' ? 'Grenze erreicht'
    : `${kosten.unterEinem && kosten.prozent < 1 ? 'unter 1' : kosten.prozent.toLocaleString('de-AT')} % der persönlichen Grenze verwendet`;
  if (vomKontingent) return `${vomKontingent.unterEinem && vomKontingent.prozent < 1 ? 'unter 1' : vomKontingent.prozent.toLocaleString('de-AT')} % des Kontingents verwendet`;
  // Plan 1a Welle 1 (W1-G06): „% Ihrer Recherchen im Monat“ statt „des Recherchekontingents“.
  if (recherchen) return `${recherchen.prozent.toLocaleString('de-AT')} % Ihrer Recherchen im Monat`;
  if (stand.organisation?.paket) return stand.organisation.gesperrt ? firmenNutzung ?? '–' : '–';
  return 'Keine persönliche Grenze';
}

/** Nachbesserung I/Z-05 (VORAB-MESSUNG 7.2 Nr 6; GB-02; welle-2/nachbesserung/AUFTEILUNG.md A-10, W-18): die Prüfung am Feld
 *  von „Name ändern“ nach der Regel des Servers (PATCH /api/auth/profile, backend/src/routes/account.ts: Vor- und Nachname ohne
 *  Leerzeichen am Rand 1 bis 100 Zeichen; die Länge begrenzt schon maxLength). fehler: je Feld der Satz, wenn es leer ist (die
 *  Seite zeigt ihn erst nach Verlassen des Felds); fehlt: was leer ist, in der Reihenfolge des Formulars. Steht hier bei den
 *  reinen Funktionen des Profils, damit Prüfungen sie ohne die ganze Seite laden. */
export const NAME_FEHLER = { vorname: 'Bitte einen Vornamen eintragen.', nachname: 'Bitte einen Nachnamen eintragen.' } as const;
export function namePruefung(eingabe: { vorname: string; nachname: string }): { fehler: { vorname?: string; nachname?: string }; fehlt: string[] } {
  const fehler = { vorname: eingabe.vorname.trim() ? undefined : NAME_FEHLER.vorname, nachname: eingabe.nachname.trim() ? undefined : NAME_FEHLER.nachname };
  return { fehler, fehlt: [fehler.vorname ? 'Vorname' : '', fehler.nachname ? 'Nachname' : ''].filter(Boolean) };
}

const ZUGANG: ProfilWeg = { slug: 'zugang', text: 'Mein Zugang' };
// Plan 1a MA-02: von der Rolle zur einen Erklärung in der Hilfe (Thema „Wer darf was“, hilfeThemen.ts rollen-woerter).
// Plan 1a Welle 2 (Ä-09 Punkt 4): mit dem Anker des Themas, damit es gleich aufgeklappt öffnet; „Mein Zugang“ nimmt denselben
// Weg (pages/accessSummary.ts VERWALTENDE_TEXTE).
export const WER_DARF_WAS: ProfilWeg = { slug: `${HILFE_SLUG}#${ROLLEN_HILFE_ANKER}`, text: 'Wer darf was?' };
// Plan 1a E08 (Funktionsprobe fd F1): ein Plattformkonto hat keinen Tarif; „Mein Zugang“ nennt den Satz dazu.
export const KEIN_TARIF_PLATTFORM = 'Kein Tarif (Plattformkonto)';
const PLATTFORMKONTO = 'Plattformkonto';

/** Die Zeilen der „Zugehörigkeit“ im Profil. Profil-Nachbau F-02 (BAU-EINSTELLUNGEN.md B4.3, PF-08): genau vier Zeilen, je mit
 *  Weg – Organisation, Rolle, Lizenzplatz, Konto seit. Tarif, Zwei-Faktor, Nutzung und vorherige Anmeldung stehen auf ihren
 *  Seiten („Mein Zugang“, „Sicherheit“, „Nutzung“). In einer Sicht sind Organisation, Rolle und Platz nicht lesbar (F-08-6);
 *  „Konto seit“ gehört dem echten Konto (VE-20). */
export function profilZeilen(eingabe: {
  begriffe: ProfilBegriffe;
  sicht: boolean;
  access: PersonalAccess | null | undefined;
  account: Pick<AccountFlags, 'rolle'> | null | undefined;
  createdAt: string | null | undefined;
}): ProfilZeile[] {
  const { begriffe, sicht, access, account } = eingabe;
  const lesbar = (text: string) => sicht ? NICHT_LESBAR : text;
  // Schlusslauf 2, N10: ein Plattformkonto ohne Rolle in einer Organisation heißt „Plattformkonto“ statt „Ohne …“ (das
  // sagt schon die Zeile der Organisation), ohne Weg zur Hilfe der Firmenrollen.
  const plattformOhneRolle = access?.status === 'PLATFORM_BLOCKED' && kontoRollenWort(account?.rolle, begriffe) === null;
  return [
    { titel: begriffe.tenantLabel, wert: lesbar(organisationText(access, begriffe)), weg: ZUGANG },
    { titel: 'Rolle', wert: lesbar(plattformOhneRolle ? PLATTFORMKONTO : rolleText(account?.rolle, begriffe, access?.organization?.kind === 'SOLO')),
      weg: plattformOhneRolle ? null : WER_DARF_WAS },
    { titel: 'Lizenzplatz', wert: lesbar(platzText(access)), weg: ZUGANG },
    { titel: 'Konto seit', wert: datumText(eingabe.createdAt), weg: { slug: 'daten', text: PROFIL_TEXTE.kontoSeitWeg } },
  ];
}

/** Wie UserMenu.tsx:104-109 (PF-08, B4.1): erste Buchstaben von Vor- und Nachname, sonst der erste Buchstabe der E-Mail-Adresse,
 *  groß. */
export function initialen(konto: { firstName?: string | null; lastName?: string | null; email: string }): string {
  const first = konto.firstName?.[0] || '';
  const last = konto.lastName?.[0] || '';
  return (first + last).toUpperCase() || konto.email[0].toUpperCase();
}

/** Die Zeile unter dem Namen im Kopf des Profils (PF-08, B4.1): „E-Mail · Firma · Rolle“ mit „ · “; die Firma
 *  (organisationText) nur mit Organisation, die Rolle (kontoRollenWort) entfällt bei null; in einer Sicht nur die E-Mail-Adresse
 *  (Firma und Rolle sind dort nicht lesbar). */
export function kopfZeile(eingabe: { email: string; sicht: boolean; access: PersonalAccess | null | undefined;
  account: Pick<AccountFlags, 'rolle'> | null | undefined; begriffe: ProfilBegriffe }): string {
  const { email, sicht, access, account, begriffe } = eingabe;
  if (sicht) return email;
  const firma = access?.organization ? organisationText(access, begriffe) : null;
  const rolle = kontoRollenWort(account?.rolle, begriffe);
  return [email, firma, rolle].filter(Boolean).join(' · ');
}
